import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../context/userContext";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import Tooltip from "../Common/tooltip.component";
import FileIcon from "../../assets/icons/file_icon.svg";
import { ReactComponent as RocketIcon } from "../../assets/icons/rocket_icon.svg";
import { ReactComponent as ProfileIcon } from "../../assets/icons/profile_placeholder_icon.svg";
import faviconImage from "../../assets/favicon.png";
import { ReactComponent as AddIcon } from "../../assets/icons/close_icon.svg";
import { ReactComponent as EmptyFileIcon } from "../../assets/icons/no_data_icon.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info_icon.svg";
import { ReactComponent as DownloadIcon } from "../../assets/icons/download_icon.svg";
import { ReactComponent as DocIcon } from "../../assets/icons/file_icon.svg";

function AddNewSession({ fetchData }) {
  const { userData, setUserData, fetchDashboardData, scrollHeight, handleGenerateSampleQuestions } = useUser();

  const [stepNum, setStepNum] = useState(2);

  const [sessionName, setSessionName] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);

  const [selectedFiles, setSelectedFiles] = useState([]);

  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const navigate = useNavigate();

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const acceptedImageTypes = [
        "image/gif",
        "image/jpeg",
        "image/png",
        "image/svg+xml",
      ];
      if (!acceptedImageTypes.includes(file.type)) {
        alert("Please select a valid image file.");
        return;
      }

      // Check if the file size is less than or equal to 2MB
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (file.size > maxSize) {
        alert("File size exceeds the limit of 2MB.");
        return;
      }

      // Use FileReader to convert the file to a data URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageSrc(reader.result);
      };
      reader.readAsDataURL(file);
      setSelectedImage(file);
    }
  };

  const handleSessionNameChange = (e) => {
    setSessionName(e.target.value);
  };

  const handleSessionNameAndImgSubmit = () => {
    setStepNum(3);
    if(!selectedImage) {
      setImageSrc(faviconImage)
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const filteredFiles = [];
    const allowedExtensions = [
      ".html",
      ".pdf",
      ".epub",
      ".rtf",
      ".odt",
      ".docx",
      ".txt",
      ".xlsx",
      ".xlsm",
      ".csv",
      ".ppt",
      ".md",
      ".pptx",
    ];
    const maxSize = 10 * 1024 * 1024; // 10MB
    for (const file of acceptedFiles) {
      const fileExtension = file.name.split(".").pop();
      if (!allowedExtensions.includes(`.${fileExtension}`)) {
        alert(
          `The file type (${file.name}) that you are trying to upload is not allowed.`
        );
        continue; //so that this file is not pushed into the filtered files and so that leaving the invalid files rest all files are uploaded.
        // return;
      }
      if (file.size > maxSize) {
        alert(
          `The file size of ${file.name} exceeds the maximum allowed size (10MB).`
        );
        continue;
        //return
      }
      filteredFiles.push(file)
    }
    setStepNum(4);
    setSelectedFiles((prevFiles) => [...prevFiles, ...filteredFiles]);
  }, []);
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: true,
  });

  const handleSignOut = () => {
    localStorage.removeItem("accessToken");
    setUserData(null);
    navigate("/");
  };

  const handleCreateNewSession = async () => {
    setStepNum(0);

    if (sessionName === "") {
      alert("Please enter session name.");
      return;
    }

    if (selectedFiles.length === 0) {
      alert("Please select at least one file.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("email", userData.email);
      formData.append("qr_name", sessionName);

      selectedFiles.forEach((file) => {
        formData.append("file", file);
      });

      if (!selectedImage) {
        const blob = await fetch(faviconImage).then(response => response.blob());
        const file = new File([blob], 'favicon.png', { type: 'image/png' }); 
        formData.append("logo", file);
      } else {
        formData.append("logo", selectedImage);
      }

      setUploading(true);

      const response = await axios.post(
        "https://soliboat.solidaridadasia.com/process_file",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${userData.token}`,
          },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const progressPercent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setProgress(progressPercent);
          },
        }
      );

      const responseData = response.data.data;

      if (responseData && responseData.length > 0) {
        window.setTimeout(() => {
          setUserData((prevUserData) => ({
            ...prevUserData,
            session_data: Array.isArray(prevUserData.session_data)
              ? [
                  ...prevUserData.session_data,
                  {
                    logo_url: responseData[0].logo_url,
                    qr_name: responseData[0].qr_name,
                    qr_url: responseData[0].qr_url,
                    session_id: responseData[0].session_id,
                    chat_url: responseData[0].chat_url,
                  },
                ]
              : [
                  {
                    logo_url: responseData[0].logo_url,
                    qr_name: responseData[0].qr_name,
                    qr_url: responseData[0].qr_url,
                    session_id: responseData[0].session_id,
                    chat_url: responseData[0].chat_url,
                  },
                ],
            session_id: Array.isArray(prevUserData.session_id)
              ? [...prevUserData.session_id, responseData[0].session_id]
              : [responseData[0].session_id],
          }));
          console.log(responseData[0]);
        }, 0);
      }

      // Retry fetching data up to 3 times with a 1-second interval
      let retries = 0;
      const maxRetries = 3;

      const retryFetch = async () => {
        try {
          await fetchData();
        } catch (error) {
          retries++;
          if (retries < maxRetries) {
            setTimeout(() => retryFetch(), 1000);
          } else {
            alert("Error fetching data. Please sign in again.");
            handleSignOut();
          }
        }
      };

      await retryFetch();
      await fetchDashboardData();
      await handleGenerateSampleQuestions(responseData[0].session_id);
    } catch (error) {
      if (error.response && error.response.status === 413) {
        alert(
          "Something went wrong, unable to parse one or more file(s) you are trying to upload."
        );
      } else {
        alert("Error uploading file. Check if the file is not empty and try again!");
        console.error("Error uploading file:", error);
      }
    } finally {
      setUploading(false);
      setProgress(0);
      setSelectedFiles([]);
    }
  };

  const clearButtonText =
    window.innerWidth > 768 ? "Clear Selected Files" : "Clear Files";

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center w-full md:ml-2">
        <div className="w-full md:w-4/5">
          <p className="md:mt-2 text-neutral-600 text-lg md:text-xl xl:text-2xl font-semibold">
            Add New Session
          </p>
          <p className="w-[90%] my-1 text-xs sm:text-base text-gray-400 font-medium">
            Give a name to your session and add your files here to start using
            our virtual assistant platform.
          </p>
        </div>
        {/* <div className="hidden md:flex md:justify-end mt-2 md:w-1/5">
          <button
            className="mr-4 px-3 py-2 bg-primary text-sm sm:text-base text-white rounded-md"
            onClick={handleCreateNewSession}
          >
            Create New Session
          </button>
        </div> */}
      </div>

      <div className="mt-2 border-b border-neutral-200" />

      <p className="ml-[2px] md:ml-2 my-2 text-neutral-600 text-base md:text-lg font-medium">
        Enter session name & image :
      </p>
      <div className="mt-2 border-b border-neutral-200" />
      <div className="flex justify-between items-center md:w-1/2 lg:w-1/3">
        <div className="flex flex-col items-center w-1/4 mr-2 md:mr-0">
          <label
            htmlFor="fileInput"
            className="relative flex justify-center items-center cursor-pointer"
          >
            {selectedImage === null ? (
              <ProfileIcon
                className={`rounded-full w-16 h-16 md:w-20 md:h-20 border ${
                  stepNum === 2 ? "z-[100]" : ""
                }`}
              />
            ) : (
              <img
                src={imageSrc}
                alt={userData.companyName}
                className={`mt-2 rounded-full w-[72px] h-[72px] md:w-20 md:h-20 border ${
                  stepNum === 2 ? "z-[100]" : ""
                }`}
              />
            )}
            <AddIcon
              title="Add Button"
              fill="white"
              className={`absolute top-1 right-1 w-5 h-5 cursor-pointer rounded-full bg-gray-300 rotate-45 hover:bg-neutral-400 hover:scale-[102%] ${
                stepNum === 2 ? "z-[100]" : ""
              }`}
            />
            <input
              type="file"
              id="fileInput"
              accept=".tiff, .svg, .jpeg, .jpg, .png"
              onChange={handleImageChange}
              className="hidden"
              ref={fileInputRef}
            />
          </label>
        </div>
        <div className="w-3/4">
          <label className="md:ml-2 flex flex-col">
            <input
              type="text"
              value={sessionName}
              placeholder="Enter session name"
              onChange={(event) => handleSessionNameChange(event)}
              className={`h-9 md:h-10 my-2 px-2 leading-tight text-neutral-700 placeholder-body-color border border-neutral-300 rounded-lg focus:outline-none focus:border-primary ${
                stepNum === 2 ? "z-[100]" : ""
              }`}
            />
            <button
              className={`px-3 py-2 bg-primary text-sm sm:text-base text-white rounded-md ${
                stepNum === 2 ? "pulse-button z-[100]" : ""
              } ${!sessionName ? "cursor-not-allowed" : ""}`}
              onClick={handleSessionNameAndImgSubmit}
              disabled={!sessionName}
              title={!sessionName && "Enter a session name!"}
            >
              Save
            </button>
          </label>

          {(userData?.session_data || []).length === 0 && ((userData?.deleted_session_id || []).length === 0) && stepNum === 2 && (
            <div
              className="absolute top-0 left-0 w-full h-[1856px] bg-gray-800 bg-opacity-50 z-50"
              style={{ height: scrollHeight }}
            >
              <div className="absolute top-[13%] left-[5%] md:top-[15%] md:left-[30%] lg:top-[26%] lg:left-[40%] xl:top-[20%] xl:left-[55%] flex items-center p-8 bg-white rounded-3xl">
                <div>
                  <p className="mb-1 md:mb-2 text-sm font-semibold text-blue-600">
                    Next step...
                  </p>
                  <h2 className="mb-2 md:mb-3 font-semibold text-lg md:text-2xl text-black">
                    Step 2: Give a name to <br /> your Session.
                  </h2>
                  <p className="text-sm">
                    Enter a suitable name
                    <br className="hidden md:block" /> and click on the{" "}
                    <br className="block md:hidden" />
                    <span className="font-semibold">"Save"</span> button.
                  </p>
                  <div className="flex mt-4">
                    <div className="w-2 h-2 mr-1 bg-blue-600 rounded-full"></div>
                    <div className="w-2 h-2 mr-1 bg-gray-100 rounded-full"></div>
                    <div className="w-2 h-2 mr-1 bg-gray-100 rounded-full"></div>
                  </div>
                </div>
                <div>
                  <RocketIcon className="hidden md:block w-36 h-36 p-2 ml-6 border rounded-full" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 border-b border-neutral-200" />

      <div className="md:mt-2">
        <div className="mt-3 md:mt-6 rounded-lg z-[100]">
          <div
            {...getRootProps({
              className: `relative flex items-center justify-center p-2 bg-neutral-50 border-dashed border-2 border-neutral-300 rounded-xl ${
                stepNum === 3 ? "z-[100]" : ""
              }`,
            })}
          >
            <input {...getInputProps()} />
            <div className="flex items-center">
              <img
                src={FileIcon}
                alt="File Icon"
                className="h-8 md:h-12 m-2 md:m-4"
              />
              <span>
                <p className="hidden md:block font-semibold text-neutral-700">
                  Drag & drop your file here
                </p>
                <div className="flex items-center">
                  <p className="ml-2 md:ml-0 text-sm md:text-base">
                    File formats we support.
                  </p>
                  <Tooltip 
                    className="bottom-[105%] md:bottom-[120%] left-[-50%] md:left-[54%]"
                    width="240px" 
                    content="Supported file types: .html, .pdf, .epub, .rtf, .odt, .docx, .txt, .xlsx, .xlsm, .csv, .ppt, .md"
                  >
                    <InfoIcon
                      className="w-5 h-5 ml-1 cursor-pointer"
                      fill="none"
                      stroke="#6495ED"
                    />
                  </Tooltip>
                </div>
              </span>
            </div>
            <div className="ml-3 md:ml-6">
              <button
                className="flex items-center px-2 md:px-6 py-2 bg-blue-50 border-2 border-blue-200 rounded"
                onClick={open}
              >
                <DownloadIcon className="w-5 h-5 p-[3px] border border-neutral-700 rounded-xl rotate-180" />
                <span className="ml-2 text-sm md:text-base font-medium text-neutral-700 hover:underline underline-offset-2">
                  Browse
                </span>
              </button>
            </div>
          </div>

          {(userData?.session_data || []).length === 0 &&
            selectedFiles.length === 0 && ((userData?.deleted_session_id || []).length === 0) &&
            stepNum === 3 && (
              <div
                className="absolute top-0 left-0 w-full bg-gray-800 bg-opacity-50 z-50"
                style={{ height: scrollHeight }}
              >
                <div className="absolute top-[20%] left-[5%] md:top-[27%] md:left-[5%] lg:top-[30%] lg:left-[50%] xl:top-[30%] xl:left-[55%] flex items-center p-8 bg-white rounded-3xl">
                  <div>
                    <p className="mb-1 md:mb-2 text-sm font-semibold text-blue-600">
                      Next step...
                    </p>
                    <h2 className="mb-2 md:mb-3 font-semibold text-lg md:text-2xl text-black">
                      Step 3: Upload file(s) <br /> to your Session.
                    </h2>
                    <p className="text-sm">
                      Drag and drop or click on the <br />
                      <span className="font-semibold">"Browse"</span> button to
                      upload files.
                    </p>
                    <div className="flex mt-4">
                      <div className="w-2 h-2 mr-1 bg-gray-100 rounded-full"></div>
                      <div className="w-2 h-2 mr-1 bg-blue-600 rounded-full"></div>
                      <div className="w-2 h-2 mr-1 bg-gray-100 rounded-full"></div>
                    </div>
                  </div>
                  <div>
                    <RocketIcon className="hidden md:block w-36 h-36 p-2 ml-6 border rounded-full" />
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Display selected files */}
        {selectedFiles.length > 0 && (
          <div className="mt-4 p-2 md:p-4 border border-neutral-300 rounded-xl">
            <div className="flex items-center justify-between md:justify-normal md:py-2">
              <div className="md:w-4/5">
                <p className="ml-1 md:ml-2 text-neutral-600 text-lg font-medium">
                  Selected Files:
                </p>
              </div>
              <div className="md:w-1/5 flex justify-end">
                <button
                  className="py-2 px-3 text-sm md:text-base font-medium md:font-semibold text-blue-500 border border-blue-100 rounded-lg hover:opacity-80 hover:underline"
                  onClick={() => setSelectedFiles([])}
                >
                  {clearButtonText}
                </button>
              </div>
            </div>

            <div className="border border-neutral-100 mt-2 mb-4" />
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex justify-between items-center mb-2 px-2 md:px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-md"
              >
                <div className="w-full flex items-center pr-4">
                  <DocIcon className="min-w-[28px] w-7 md:w-8 h-7 md:h-8" />
                  <div className="flex items-center line-clamp-1">
                    <p className="ml-3 text-base md:text-lg text-blue-400 leading-tight line-clamp-1 cursor-pointer">
                      {file.name}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Progress bar */}
        {uploading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center z-50">
            <div className="relative flex flex-col bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between">
                <p className="text-slate-700 text-lg font-semibold">
                  Uploading ...
                </p>
                <p className="text-blue-700 text-lg font-semibold">
                  {progress}%
                </p>
              </div>
              <div className="w-48 h-4 bg-blue-200 mt-2 rounded-full">
                <div
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <InfoIcon className="w-6 h-6 mr-1" fill="white" />
              <p className="text-neutral-50">
                This might take a few moments ...
              </p>
            </div>
          </div>
        )}

        {selectedFiles.length === 0 && (
          <>
            <div className="border border-neutral-100 mt-2 mb-4" />

            <div className="mt-6 rounded-lg">
              <div className="flex flex-col items-center justify-center py-8 bg-neutral-50 border border-neutral-300 rounded-xl">
                <EmptyFileIcon className="h-32 md:h-48 mb-2" />
                <h1 className="text-slate-900 text-base md:text-xl font-semibold">
                  No files selected...
                </h1>
                <p className="mx-2 my-1 text-center text-xs sm:text-sm md:text-base text-neutral-700">
                  You have not selected any file yet. Please select atleast one
                  file.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="mt-4 border-b border-neutral-200" />
      <div className="flex justify-end mt-2 md:mt-3">
        <button
          className={`px-3 py-2 bg-primary text-sm sm:text-base text-white rounded-md ${
            stepNum === 4 ? "pulse-button z-[100]" : ""
          }`}
          onClick={handleCreateNewSession}
        >
          Create New Session
        </button>

        {(userData?.session_data || []).length === 0 &&
          selectedFiles.length > 0 && ((userData?.deleted_session_id || []).length === 0) &&
          stepNum === 4 && (
            <div
              className="absolute top-0 left-0 w-full bg-gray-800 bg-opacity-50 z-50"
              style={{ height: scrollHeight }}
            >
              <div className="absolute top-[36%] left-[5%] md:top-[46%] md:left-[6%] lg:top-[50%] lg:left-[6%] xl:top-[48%] xl:left-[8%] flex items-center p-8 bg-white rounded-3xl">
                <div>
                  <p className="mb-1 md:mb-2 text-sm font-semibold text-blue-600">
                    Next step...
                  </p>
                  <h2 className="mb-2 md:mb-3 font-semibold text-lg md:text-2xl text-black">
                    Step 4: Create <br /> your Session.
                  </h2>
                  <p className="text-sm">
                    Hit the{" "}
                    <span className="font-semibold">"Create New Session"</span>{" "}
                    button <br /> to create a new session.
                  </p>
                  <div className="flex mt-4">
                    <div className="w-2 h-2 mr-1 bg-gray-100 rounded-full"></div>
                    <div className="w-2 h-2 mr-1 bg-gray-100 rounded-full"></div>
                    <div className="w-2 h-2 mr-1 bg-blue-600 rounded-full"></div>
                  </div>
                </div>
                <div>
                  <RocketIcon className="hidden md:block w-36 h-36 p-2 ml-6 border rounded-full" />
                </div>
              </div>
            </div>
          )}
      </div>
    </>
  );
}

export default AddNewSession;
